import { gettext } from '../../services'
import { route as routeAPI } from '../../api'
import { showLoginModal } from '../authentication'
import { CATEGORIES, SURFACES } from './settings'
import sessionData from '@bikemap/js/services/sessionData'

/**
 * Initialize simple interactive functionality of route tiles. Use this helper for
 * server-side rendered routes lists on basic pages without React components or with
 * minimal JS.
 * @param {HTMLUListElement} [routesList] A certain routes list, otherwise all route items
 */
export function initRouteItems(routesList) {
    const container = routesList || document
    const routeItems = container.querySelectorAll('.route-item')
    routeItems.forEach(item => initRouteItem(/** @type {HTMLElement} */(item)))
}

/**
 * Get a list of bike type options.
 * Useful for forms.
 * @type {{label: (string), value: (string), analytics: (Object)}[]}
 */
export const bikeTypesList = [2, 3, 1].map(id => ({
    value: id,
    label: CATEGORIES[id].label,
    analytics: {
        event: 'select_bike_type',
        'bike-type': CATEGORIES[id].analytics,
    },
}))

/**
 * Get a list of road surface types.
 * Useful for forms.
 * @type {{label: (string), value: (string), analytics: (Object)}[]}
 */
export const surfacesList = [1, 2, 3].map(id => ({
    value: id,
    label: SURFACES[id].label,
    analytics: {
        event: 'select_surface_type',
        'surface-type': SURFACES[id].analytics,
    },
}))

/**
 * Update the favorite info element according to isFavorite state.
 * @param {HTMLDivElement} element
 * @param {boolean} isFavorite
 */
export function setRouteFavoriteInfoState(element, isFavorite) {
    const favoriteInfoIcon = element.querySelector('i')
    const favorButton = element.querySelector('button')

    favoriteInfoIcon.classList.toggle('i-heart', isFavorite)
    favoriteInfoIcon.classList.toggle('i-heart-empty', !isFavorite)
    element.classList.toggle('is-favorite', isFavorite)
    setFavoriteButtonTooltip(favorButton, isFavorite)
}

/**
 * Handle unauthenticated users trying to favor a route.
 */
export function handleUnauthenticatedRouteFavor() {
    showLoginModal({
        header: null,
        title: gettext('Sign up for free and add routes to your favorites'),
    })
}

/**
 * Initialize interactive functionality of a route item.
 * @param {HTMLElement} routeItem
 */
async function initRouteItem(routeItem) {
    const favorButton = /** @type {HTMLElement} */ (routeItem.querySelector('.favorite-info button'))
    const isLoggedIn = await sessionData.isLoggedIn()

    const isFavorite = isLoggedIn && routeItem.dataset.favorite === 'True'
    setFavoriteButtonTooltip(favorButton, isFavorite)

    favorButton.addEventListener('click', isLoggedIn ? () => {
        toggleFavorite(routeItem)
    } : handleUnauthenticatedRouteFavor)
}

/**
 * Toggle the favorite status on a route item and all items representing this route
 * (with class "route-[id]") and update on the server.
 * @param {HTMLElement} routeItem Route item element that triggered the action
 */
function toggleFavorite(routeItem) {
    const isFavorite = routeItem.dataset.favorite === 'True'
    const routeId = parseInt(routeItem.dataset.id)

    if (isFavorite) {
        routeAPI.unfavor(routeId)
    } else {
        routeAPI.favor(routeId)
    }

    const sameRouteItems = document.querySelectorAll('.route-item.route-' + routeId)
    sameRouteItems.forEach(routeItem => {
        setRouteItemFavoriteState(/** @type {HTMLElement} */(routeItem), !isFavorite)
        changeRouteItemFavoriteCount(routeItem, isFavorite ? -1 : 1)
    })
}

/**
 * Set a route item's favorite state.
 * @param {HTMLElement} routeItem
 * @param {boolean} isFavorite
 */
function setRouteItemFavoriteState(routeItem, isFavorite) {
    const favoriteInfoElement = routeItem.querySelector('.favorite-info')
    setRouteFavoriteInfoState(favoriteInfoElement, isFavorite)
    routeItem.dataset.favorite = isFavorite ? 'True' : 'False'
}

/**
 * Change a route item's favorite count.
 * @param {Element} routeItem
 * @param {number} change Number added to the favorite count
 */
function changeRouteItemFavoriteCount(routeItem, change) {
    const favoriteCountElement = routeItem.querySelector('.favorite-count')
    const favoriteCount = parseInt(favoriteCountElement.textContent)
    favoriteCountElement.textContent = '' + (favoriteCount + change)
}

/**
 * Set the right tooltip for the favor button by favorite state.
 * @param {HTMLButtonElement} favoriteButton
 * @param {boolean} isFavorite
 */
function setFavoriteButtonTooltip(favoriteButton, isFavorite) {
    const tooltip = isFavorite ? gettext('Remove from favorites') : gettext('Add to favorites')
    favoriteButton.dataset.tooltip = tooltip
    favoriteButton.setAttribute('aria-label', tooltip)
}
